// Typed analytics event catalog. Props are whitelisted primitives only — NO PII,
// no free text, no record content. `sanitizeProps` enforces the whitelist at runtime
// and a unit test asserts every event has an allowed-props entry (guards PII creep).

export type AnalyticsEvent =
  | { name: 'signup'; props?: Record<string, never> }
  | { name: 'generation_started'; props: { firstBuild: boolean } }
  // `compileFailures` is only meaningful for the mini-app/translate local-build
  // paths (a server-engine build never calls the local compile gate) — those
  // call sites pass it, the plain engine path omits it, and it's optional here
  // so both are valid without a third event name.
  | { name: 'generation_completed'; props: { firstBuild: boolean; ms: number; compileFailures?: number } }
  | { name: 'generation_failed'; props: { reason: string; compileFailures?: number } }
  | { name: 'generation_cancelled'; props?: Record<string, never> }
  | { name: 'regeneration'; props?: Record<string, never> }
  | { name: 'manual_edit'; props: { property: string; elementType: string } }
  | { name: 'publish'; props: { personal: boolean } }
  | { name: 'export'; props: { format: 'csv' | 'json' } }
  | { name: 'app_opened'; props: { appId: string } }
  | { name: 'data_entry'; props: { entity: string } };

export type EventName = AnalyticsEvent['name'];

/** Allowed prop keys per event — the no-PII whitelist. */
export const EVENT_PROPS: Record<EventName, string[]> = {
  signup: [],
  generation_started: ['firstBuild'],
  generation_completed: ['firstBuild', 'ms', 'compileFailures'],
  generation_failed: ['reason', 'compileFailures'],
  generation_cancelled: [],
  regeneration: [],
  manual_edit: ['property', 'elementType'],
  publish: ['personal'],
  export: ['format'],
  app_opened: ['appId'],
  data_entry: ['entity'],
};

type Primitive = string | number | boolean;

function isPrimitive(v: unknown): v is Primitive {
  return typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean';
}

/** Keep only whitelisted keys with primitive (string/number/boolean) values. */
export function sanitizeProps(
  name: EventName,
  props: Record<string, unknown> | undefined,
): Record<string, Primitive> {
  const allowed = EVENT_PROPS[name] ?? [];
  const out: Record<string, Primitive> = {};
  if (!props) return out;
  for (const key of allowed) {
    const v = props[key];
    if (isPrimitive(v)) out[key] = v;
  }
  return out;
}
